import { useEffect, useState } from 'react';
import { channelsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, CheckCircle2, History, Inbox, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import HelpButton from '@/components/HelpButton';
import { fechaYHora } from '@/lib/fechas';

/**
 * Cómo entra lo nuevo a un canal de Microsoft 365 (`config.ingreso`).
 *
 * **Por qué (2026-10-19).** El correo solo entraba por aviso de Microsoft, y en la VM de Pritty el
 * aviso no llega: Cloudflare le hace un desafío a Microsoft. Diego pidió poder elegir por canal
 * entre un **barrido** cada N minutos, que anda en cualquier red, y el **aviso**, para activarlo
 * sin tocar código cuando las reglas de seguridad lo permitan.
 *
 * Pasar a aviso crea la suscripción en el momento. Si Microsoft la rechaza, el backend devuelve el
 * motivo y el canal sigue como estaba: acá se muestra ese motivo y no se cambia la selección
 * guardada.
 *
 * Reemplaza los botones «Activar monitoreo inbox/chat» y «Desactivar», que pedían entender qué es
 * una suscripción de Graph para saber si el canal recibía algo.
 */

function haceCuanto(iso) {
  if (!iso) return null;
  const seg = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (Number.isNaN(seg)) return null;
  if (seg < 0) {
    const min = Math.ceil(-seg / 60);
    return min <= 1 ? 'en menos de un minuto' : `en ${min} min`;
  }
  if (seg < 60) return 'hace instantes';
  const min = Math.round(seg / 60);
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `hace ${h} h`;
  return fechaYHora(iso);
}

function OpcionDeModo({ valor, elegido, onElegir, titulo, children, deshabilitada, testId }) {
  const activa = elegido === valor;
  return (
    <label
      className={`flex items-start gap-2 p-2.5 rounded-sm border cursor-pointer transition-colors ${
        activa ? 'border-indigo-400 bg-white shadow-sm' : 'border-zinc-200 bg-white/60 hover:border-zinc-300'
      } ${deshabilitada ? 'opacity-60 cursor-not-allowed' : ''}`}
      data-testid={testId}
    >
      <input
        type="radio"
        className="mt-0.5"
        checked={activa}
        disabled={deshabilitada}
        onChange={() => onElegir(valor)}
      />
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-xs font-medium text-zinc-800">{titulo}</p>
        {children}
      </div>
    </label>
  );
}

export default function IngresoDeM365({ channel, onChannelUpdated }) {
  const esTeams = channel?.channel_type === 'teams';
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [modo, setModo] = useState('barrido');
  const [cada, setCada] = useState(2);
  const [guardando, setGuardando] = useState(false);
  const [horas, setHoras] = useState(24);
  const [sincronizando, setSincronizando] = useState(false);
  const [ultimaSync, setUltimaSync] = useState(null);

  const tomarEstado = (datos) => {
    setEstado(datos);
    setModo(datos?.ingreso?.modo || 'barrido');
    setCada(datos?.ingreso?.cada_minutos || 2);
    const def = datos?.inbox_sync_settings?.default_hours;
    if (typeof def === 'number' && def > 0) setHoras(def);
  };

  const cargar = async () => {
    if (!channel?.channel_id) return;
    setCargando(true);
    try {
      const res = await channelsAPI.getGraphStatus(channel.channel_id);
      tomarEstado(res.data);
    } catch {
      setEstado(null);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    setUltimaSync(null);
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel?.channel_id]);

  const guardado = estado?.ingreso || {};
  const limites = estado?.ingreso_limites || { cada_minutos_min: 1, cada_minutos_max: 60, minutos_de_respaldo: 15 };
  const entradaApagada = estado && !estado.inbound_enabled;
  const sinUrlPublica = estado && !estado.webhook_configured;
  const cadaNum = parseInt(cada, 10);
  const cadaValido = !Number.isNaN(cadaNum) && cadaNum >= limites.cada_minutos_min && cadaNum <= limites.cada_minutos_max;
  const cambio = modo !== guardado.modo || (modo === 'barrido' && cadaNum !== guardado.cada_minutos);
  const barrido = estado?.barrido || {};
  const maxHoras = estado?.inbox_sync_settings?.max_hours ?? 72;
  const que = esTeams ? 'el chat' : 'la bandeja';

  const aplicar = async () => {
    if (modo === 'barrido' && !cadaValido) {
      toast.error(`El barrido va de ${limites.cada_minutos_min} a ${limites.cada_minutos_max} minutos`);
      return;
    }
    setGuardando(true);
    try {
      const res = await channelsAPI.cambiarIngreso(channel.channel_id, {
        modo,
        cada_minutos: modo === 'barrido' ? cadaNum : undefined,
      });
      tomarEstado(res.data);
      toast.success(modo === 'aviso'
        ? 'Listo: Microsoft avisa cada mensaje nuevo'
        : `Listo: se revisa ${que} cada ${cadaNum} min`);
      onChannelUpdated?.({ ...channel, status: res.data?.status, graph_meta: res.data?.graph_meta });
    } catch (err) {
      const detalle = err.response?.data?.detail;
      toast.error(typeof detalle === 'string' ? detalle : 'No se pudo cambiar cómo entra lo nuevo', { duration: 10000 });
      // La selección vuelve a lo guardado: el backend no cambió nada.
      setModo(guardado.modo || 'barrido');
      setCada(guardado.cada_minutos || 2);
    } finally {
      setGuardando(false);
    }
  };

  const traerAnteriores = async () => {
    const h = parseInt(horas, 10);
    if (Number.isNaN(h) || h < 1 || h > maxHoras) {
      toast.error(`Elegí entre 1 y ${maxHoras} horas`);
      return;
    }
    setSincronizando(true);
    try {
      const res = esTeams
        ? await channelsAPI.syncGraphTeamsChat(channel.channel_id, h)
        : await channelsAPI.syncGraphInbox(channel.channel_id, h);
      const d = res.data || {};
      setUltimaSync({ ...d, horas: h });
      toast.success(d.processed
        ? `${d.processed} mensaje(s) nuevo(s) atendido(s)`
        : 'No había mensajes sin atender');
      await cargar();
    } catch (err) {
      const detalle = err.response?.data?.detail;
      toast.error(typeof detalle === 'string' ? detalle : `No se pudo sincronizar ${que}`);
    } finally {
      setSincronizando(false);
    }
  };

  // La línea de estado: lo primero que alguien quiere saber es si el canal está recibiendo.
  let lineaDeEstado = null;
  if (estado) {
    const resumen = barrido.ultimo_resumen || {};
    const ultimo = haceCuanto(barrido.ultimo_at);
    if (entradaApagada) {
      lineaDeEstado = { tono: 'alerta', texto: 'La entrada de Microsoft 365 está apagada en este servidor: no entra nada.' };
    } else if (barrido.ultimo_error) {
      lineaDeEstado = { tono: 'alerta', texto: `Último barrido con problemas: ${barrido.ultimo_error}` };
    } else if (guardado.modo === 'aviso') {
      const vence = estado.subscriptions?.[esTeams ? 'teams_chat' : 'email_inbox'];
      lineaDeEstado = vence
        ? { tono: 'ok', texto: `Recibe por aviso de Microsoft. Respaldo cada ${limites.minutos_de_respaldo} min${ultimo ? ` (último ${ultimo})` : ''}.` }
        : { tono: 'alerta', texto: `Configurado por aviso, pero la suscripción no está activa. Entra solo por el respaldo cada ${limites.minutos_de_respaldo} min.` };
    } else if (!barrido.ultimo_at) {
      lineaDeEstado = { tono: 'neutro', texto: `Revisa ${que} cada ${guardado.cada_minutos} min. Todavía no corrió el primer barrido.` };
    } else {
      const nuevos = resumen.primer_barrido
        ? 'primer barrido: desde ahora entra lo nuevo'
        : `${resumen.processed || 0} nuevo(s)`;
      lineaDeEstado = { tono: 'ok', texto: `Revisa ${que} cada ${guardado.cada_minutos} min. Último ${ultimo}: ${nuevos}.` };
    }
  }

  return (
    <div className="border border-zinc-200 rounded-sm p-3 space-y-3 bg-zinc-50/70" data-testid="ms-ingreso">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h5 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-600 flex items-center gap-1">
          <Inbox className="w-3.5 h-3.5" /> Cómo entra lo nuevo
        </h5>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={cargar} disabled={cargando}
                  title="Actualizar el estado">
            {cargando ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </Button>
          <HelpButton section="channels_ingreso_m365" />
        </div>
      </div>

      {!estado && cargando && <p className="text-[11px] text-zinc-500">Cargando…</p>}

      {lineaDeEstado && (
        <p
          className={`text-[11px] flex items-start gap-1.5 p-2 rounded-sm border ${
            lineaDeEstado.tono === 'ok' ? 'text-emerald-800 bg-emerald-50 border-emerald-200'
              : lineaDeEstado.tono === 'alerta' ? 'text-amber-800 bg-amber-50 border-amber-200'
                : 'text-zinc-600 bg-white border-zinc-200'}`}
          data-testid="ms-graph-status-panel"
        >
          {lineaDeEstado.tono === 'ok'
            ? <CheckCircle2 className="w-3.5 h-3.5 mt-px shrink-0" />
            : <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />}
          <span>{lineaDeEstado.texto}</span>
        </p>
      )}

      {estado && (
        <>
          <div className="grid gap-2">
            <OpcionDeModo valor="barrido" elegido={modo} onElegir={setModo} titulo="Barrido automático"
                          deshabilitada={entradaApagada} testId="ms-ingreso-barrido">
              <div className="flex items-center gap-2 text-[11px] text-zinc-600">
                <span>Revisar {que} cada</span>
                <Input
                  type="number"
                  min={limites.cada_minutos_min}
                  max={limites.cada_minutos_max}
                  value={cada}
                  onChange={(e) => { setCada(e.target.value); setModo('barrido'); }}
                  disabled={entradaApagada}
                  className={`h-7 w-16 text-xs font-mono rounded-sm ${modo === 'barrido' && !cadaValido ? 'border-red-400' : ''}`}
                  data-testid="ms-ingreso-cada-minutos"
                />
                <span>minutos</span>
              </div>
              <p className="text-[10px] text-zinc-500">
                Funciona en cualquier red: solo hace falta que el servidor salga a Microsoft.
              </p>
            </OpcionDeModo>

            <OpcionDeModo valor="aviso" elegido={modo} onElegir={setModo} titulo="Aviso de Microsoft (webhook)"
                          deshabilitada={entradaApagada || sinUrlPublica || esTeams} testId="ms-ingreso-aviso">
              <p className="text-[10px] text-zinc-500">
                Microsoft avisa al instante cada mensaje nuevo. Necesita que Microsoft pueda llegar a la URL
                pública del servidor. Igual se revisa cada {limites.minutos_de_respaldo} min por si un aviso no llega.
              </p>
              {sinUrlPublica && (
                <p className="text-[10px] text-amber-700">
                  Este servidor no tiene URL pública configurada (<span className="font-mono">MS_GRAPH_WEBHOOK_BASE_URL</span>).
                </p>
              )}
              {esTeams && (
                <p className="text-[10px] text-amber-700">
                  No disponible en Teams: el aviso del chat exige un permiso de aplicación que deja leer los chats
                  de cualquier cuenta. Teams entra por barrido, con la cuenta conectada.
                </p>
              )}
            </OpcionDeModo>
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={aplicar} disabled={!cambio || guardando || entradaApagada} data-testid="ms-ingreso-aplicar">
              {guardando && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              {modo === 'aviso' && cambio ? 'Activar aviso' : 'Aplicar'}
            </Button>
            {cambio && !guardando && (
              <span className="text-[10px] text-zinc-500">
                {modo === 'aviso'
                  ? 'Se crea la suscripción en Microsoft. Si la rechaza, el canal sigue como está.'
                  : 'Sin aplicar.'}
              </span>
            )}
          </div>

          <div className="pt-2 border-t border-zinc-200 space-y-2">
            <p className="text-[11px] font-medium text-zinc-700 flex items-center gap-1">
              <History className="w-3.5 h-3.5" /> Traer mensajes anteriores
            </p>
            <p className="text-[10px] text-zinc-500">
              Atiende lo que llegó en las últimas horas y todavía no se atendió. Lo ya respondido se saltea: no duplica respuestas.
            </p>
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">Últimas horas</Label>
                <Input
                  type="number"
                  min={1}
                  max={maxHoras}
                  value={horas}
                  onChange={(e) => setHoras(e.target.value)}
                  className="h-8 w-20 text-xs font-mono rounded-sm"
                  data-testid="ms-inbox-sync-hours"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={traerAnteriores}
                disabled={sincronizando || entradaApagada}
                data-testid={esTeams ? 'ms-teams-chat-sync-btn' : 'ms-inbox-sync-btn'}
              >
                {sincronizando && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                {esTeams ? 'Sincronizar chat' : 'Sincronizar bandeja'}
              </Button>
              <span className="text-[10px] text-zinc-400">Máximo {maxHoras} h</span>
            </div>
            {sincronizando && (
              <p className="text-[10px] text-zinc-500">Cada mensaje nuevo pasa por el agente: puede tardar unos minutos.</p>
            )}
            {ultimaSync && (
              <p className="text-[10px] text-zinc-600" data-testid="ms-sync-resultado">
                Últimas {ultimaSync.horas} h: {ultimaSync.listed_count ?? 0} encontrado(s), {ultimaSync.processed ?? 0} atendido(s),
                {' '}{ultimaSync.skipped_duplicate ?? 0} ya atendido(s) antes
                {ultimaSync.errors ? `, ${ultimaSync.errors} con error (ver Registros)` : ''}.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
